import { useMemo, useState } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { Card, DataTable, Badge, Skeleton, StatusMessage } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Filters, Pagination } from '../../components/uiChrome.jsx';
import { formatRelativeTime, CHIP_BASE, CHIP_LABEL, CHIP_TONE } from '../../utils';
import { useInstanceAudit } from '../../hooks/instance';
import { AlertTriangle, ExternalLink } from 'lucide-react';

const DATE_OPTIONS = ['1h', '24h', '7d', '30d'];
const MIN = 60_000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;
const DATE_RANGE_MS = { '1h': HOUR, '24h': DAY, '7d': 7 * DAY, '30d': 30 * DAY };
const DEFAULT_PAGE_SIZE = 10;

/**
 * Instance Detail — Audit tab (CT-4192, admin-only). Immutable per-instance
 * configuration trail. Same chrome as Logs: Card + Filters (no All filters) +
 * workspace DataTable + sticky Pagination. Newest first — the reference `now`
 * comes from the hook so the date cutoff is stable.
 */
const InstanceAuditTab = () => {
  const { t } = useTranslation('admin-studio');
  const navigate = useNavigate();
  const { instance } = useOutletContext();
  const { rows, now, isLoading, error } = useInstanceAudit(instance.instanceId);

  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState([]);
  const [eventFilter, setEventFilter] = useState([]);
  const [actorFilter, setActorFilter] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PAGE_SIZE);

  const searchProps = useMemo(
    () => ({
      value: search,
      onChange: (e) => {
        setSearch(e?.target ? e.target.value : e);
        setPage(1);
      },
      onClear: () => {
        setSearch('');
        setPage(1);
      },
      placeholder: t('integrations.instance.audit.searchPlaceholder'),
    }),
    [search, t],
  );

  const eventOptions = useMemo(() => {
    const seen = new Set();
    return rows.reduce((opts, r) => {
      if (!seen.has(r.event)) {
        seen.add(r.event);
        opts.push({ value: r.event, label: r.event });
      }
      return opts;
    }, []);
  }, [rows]);

  const actorOptions = useMemo(() => {
    const seen = new Set();
    return rows.reduce((opts, r) => {
      if (!seen.has(r.actor)) {
        seen.add(r.actor);
        opts.push({ value: r.actor, label: r.actor });
      }
      return opts;
    }, []);
  }, [rows]);

  const filtersConfig = useMemo(
    () => [
      {
        id: 'date',
        label: t('integrations.instance.audit.dateLabel'),
        multiple: false,
        value: dateRange,
        onChange: (next) => {
          setDateRange(next);
          setPage(1);
        },
        options: DATE_OPTIONS.map((v) => ({ value: v, label: t(`integrations.instance.audit.date.${v}`) })),
      },
      {
        id: 'event',
        label: t('integrations.instance.audit.eventLabel'),
        multiple: false,
        value: eventFilter,
        onChange: (next) => {
          setEventFilter(next);
          setPage(1);
        },
        options: eventOptions,
      },
      {
        id: 'actor',
        label: t('integrations.instance.audit.actorLabel'),
        multiple: false,
        value: actorFilter,
        onChange: (next) => {
          setActorFilter(next);
          setPage(1);
        },
        options: actorOptions,
      },
    ],
    [dateRange, eventFilter, actorFilter, eventOptions, actorOptions, t],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const range = dateRange[0];
    const cutoff = range ? now - DATE_RANGE_MS[range] : null;
    const eventValue = eventFilter[0];
    const actorValue = actorFilter[0];
    return rows.filter((r) => {
      if (cutoff != null && new Date(r.timestamp).getTime() < cutoff) return false;
      if (eventValue && r.event !== eventValue) return false;
      if (actorValue && r.actor !== actorValue) return false;
      if (q && !`${r.event} ${r.actor} ${r.detail}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rows, now, search, dateRange, eventFilter, actorFilter]);

  const columns = useMemo(
    () => [
      {
        key: 'timestamp',
        label: t('integrations.instance.audit.columns.timestamp'),
        headerClassName: 'w-[140px]',
        render: (v) => (
          <span className="text-[13px] text-content-secondary" title={new Date(v).toLocaleString()}>
            {formatRelativeTime(v, t, now || undefined)}
          </span>
        ),
      },
      {
        key: 'event',
        label: t('integrations.instance.audit.columns.event'),
        headerClassName: 'w-[200px]',
        render: (v) => (
          <Badge variant="neutral" size="small" className={`${CHIP_BASE} ${CHIP_TONE.accent} font-mono`}>
            <span className={CHIP_LABEL}>{v}</span>
          </Badge>
        ),
      },
      { key: 'actor', label: t('integrations.instance.audit.columns.actor') },
      { key: 'detail', label: t('integrations.instance.audit.columns.detail') },
    ],
    [t, now],
  );

  if (isLoading) return <Skeleton className="h-40 w-full" />;
  if (error) return <StatusMessage icon={AlertTriangle} title={t('common.requestFailed')} subtitle={error} />;

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * perPage;
  const pageRows = filtered.slice(pageStart, pageStart + perPage);
  const rangeLabel =
    filtered.length === 0 ? '0' : `${pageStart + 1}–${Math.min(pageStart + perPage, filtered.length)}`;

  return (
    <Card>
      <Card.Body className="flex flex-col gap-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <h3 className="text-sm font-semibold text-content">{t('integrations.instance.audit.title')}</h3>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px]">
            <span className="text-content-muted">{t('integrations.instance.audit.scope')}</span>
            {/* Deep-link into the Global Audit log pre-filtered to this instance (CT-4213). */}
            <button
              type="button"
              onClick={() =>
                navigate(
                  `/admin-studio/audit?provider=${encodeURIComponent(instance.providerSlug)}&instance=${encodeURIComponent(instance.instanceId)}`,
                )
              }
              className="inline-flex items-center gap-1 font-medium text-brand-500 hover:underline"
            >
              {t('integrations.instance.audit.workspaceLog')}
              <ExternalLink size={13} aria-hidden="true" />
            </button>
          </div>
        </div>

        <Filters
          searchProps={searchProps}
          filters={filtersConfig}
          showAllFilters={false}
          maxFilters={3}
          onClearAll={() => {
            setSearch('');
            setDateRange([]);
            setEventFilter([]);
            setActorFilter([]);
            setPage(1);
          }}
        />

        <DataTable
          variant="workspace"
          columns={columns}
          data={pageRows}
          minTableWidth={880}
          emptyMessage={
            rows.length === 0
              ? t('integrations.instance.audit.emptyNone')
              : t('integrations.instance.audit.empty')
          }
        />

        <Pagination
          rangeLabel={rangeLabel}
          totalItems={filtered.length}
          currentPage={safePage}
          totalPages={totalPages}
          perPage={perPage}
          hasPrevious={safePage > 1}
          hasNext={safePage < totalPages}
          onPrevPage={() => setPage(Math.max(1, safePage - 1))}
          onNextPage={() => setPage(Math.min(totalPages, safePage + 1))}
          onPageSizeChange={(size) => {
            setPerPage(size);
            setPage(1);
          }}
        />
      </Card.Body>
    </Card>
  );
};

export default InstanceAuditTab;
